import type {
  SizeProperty,
  SizePropertiesListResponse,
  SizePropertiesFilter,
  SizePropertiesStats,
} from '@src/types';
import { execAdmin } from '../utils';

const gql = String.raw;

interface SizePropertyGraphQL {
  id: number;
  partnerCode: string;
  code: string;
  display: string;
  synonyms: string[];
  family: string[];
  primaryValue?: string | null;
  primaryLabel?: string | null;
  secondaryValue?: string | null;
  secondaryLabel?: string | null;
  tertiaryValue?: string | null;
  tertiaryLabel?: string | null;
  createdAt: string;
  updatedAt: string;
}

interface GetSizePropertiesResponse {
  partner: {
    sizeProperties: {
      edges: { node: SizePropertyGraphQL; cursor: string }[];
      pageInfo: {
        hasNextPage: boolean;
        hasPreviousPage: boolean;
        startCursor: string | null;
        endCursor: string | null;
      };
      totalCount: number;
    };
  };
}

interface GetSizePropertiesVariables {
  partnerCode: string;
  search?: string;
  first: number;
  after: string | null;
}

const GET_SIZE_PROPERTIES_QUERY = gql`
  query getSizeProperties(
    $partnerCode: String!
    $search: String
    $first: Int
    $after: String
  ) {
    partner(code: $partnerCode) {
      sizeProperties(search: $search, first: $first, after: $after) {
        edges {
          node {
            id
            partnerCode
            code
            display
            synonyms
            family
            primaryValue
            primaryLabel
            secondaryValue
            secondaryLabel
            tertiaryValue
            tertiaryLabel
            createdAt
            updatedAt
          }
          cursor
        }
        pageInfo {
          hasNextPage
          hasPreviousPage
          startCursor
          endCursor
        }
        totalCount
      }
    }
  }
`;

export const getSizeProperties = async ({
  partnerCode,
  filter = {},
  page = 1,
  perPage = 50,
}: {
  partnerCode: string;
  filter?: SizePropertiesFilter;
  page?: number;
  perPage?: number;
}): Promise<SizePropertiesListResponse> => {
  // The API is cursor-paginated (Relay-style); its cursors are opaque but are
  // actually base64("cursor:<offset>") under the hood, so a numeric page can
  // be translated into a cursor without an extra round trip. See
  // reware-api's lib/util/cursor.ts for the matching encode/decode.
  const after = page > 1 ? btoa(`cursor:${(page - 1) * perPage - 1}`) : null;

  const variables: GetSizePropertiesVariables = {
    partnerCode,
    search: filter.searchQuery,
    first: perPage,
    after,
  };

  const data = await execAdmin<GetSizePropertiesVariables, GetSizePropertiesResponse>(
    GET_SIZE_PROPERTIES_QUERY,
    { variables }
  );

  const { edges, totalCount } = data.partner.sizeProperties;

  // Map GraphQL camelCase to frontend snake_case
  const sizeProperties: SizeProperty[] = edges.map(({ node }) => ({
    id: node.id,
    partner_code: node.partnerCode,
    code: node.code,
    display: node.display,
    synonyms: node.synonyms,
    family: node.family,
    primary_value: node.primaryValue,
    primary_label: node.primaryLabel,
    secondary_value: node.secondaryValue,
    secondary_label: node.secondaryLabel,
    tertiary_value: node.tertiaryValue,
    tertiary_label: node.tertiaryLabel,
    created_at: node.createdAt,
    updated_at: node.updatedAt,
  }));

  return {
    data: sizeProperties,
    total: totalCount,
  };
};

/**
 * Computes completeness stats over a full set of size properties. Callers
 * must supply the complete (unpaginated) list — e.g. via fetching all pages —
 * since a page-scoped subset would understate missing* counts relative to
 * `total`.
 */
export function calculateSizePropertiesStats(properties: SizeProperty[]): SizePropertiesStats {
  const total = properties.length;
  const missingDisplay = properties.filter((p) => !p.display).length;
  const missingGroup = properties.filter((p) => !p.family || p.family.length === 0).length;
  const incomplete = properties.filter((p) => !p.display || !p.family || p.family.length === 0).length;
  const completeness = total > 0 ? Math.round(((total - incomplete) / total) * 100) : 0;

  return {
    total,
    missingDisplay,
    missingGroup,
    completeness,
  };
}
